import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  Search,
  Trophy,
  Clock,
  Target,
  Star,
  TrendingUp,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/simple-select";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useSupabase } from "@/hooks/useSupabase";
import { Athlete, AthleteScore, Attempt } from "@shared/types";
import { ROUTES } from "@/lib/store";

interface AthleteResult {
  athlete: Athlete;
  score: AthleteScore;
  attempts: Attempt[];
  ranking: {
    position: number;
    total: number;
    gender: "Masculino" | "Feminino";
  };
}

export default function AthleteConsult() {
  const { store } = useSupabase();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedGender, setSelectedGender] = useState<string>("all");
  const [athletes, setAthletes] = useState<Athlete[]>([]);
  const [filteredAthletes, setFilteredAthletes] = useState<Athlete[]>([]);
  const [selectedAthlete, setSelectedAthlete] = useState<Athlete | null>(null);
  const [athleteResult, setAthleteResult] = useState<AthleteResult | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingAthletes, setIsLoadingAthletes] = useState(true);
  const [message, setMessage] = useState("");

  useEffect(() => {
    loadAthletes();
  }, [store]);

  useEffect(() => {
    filterAthletes();
  }, [athletes, searchTerm, selectedGender]);

  const loadAthletes = async () => {
    setIsLoadingAthletes(true);
    try {
      const athleteList = await store.getAthletes();
      setAthletes(athleteList);
    } catch (error) {
      console.error("Error loading athletes:", error);
      setMessage("❌ Erro ao carregar lista de atletas.");
    } finally {
      setIsLoadingAthletes(false);
    }
  };

  const filterAthletes = () => {
    let filtered = athletes;

    // Filter by search term
    if (searchTerm.trim()) {
      filtered = filtered.filter((athlete) =>
        athlete.name.toLowerCase().includes(searchTerm.toLowerCase()),
      );
    }

    // Filter by gender
    if (selectedGender !== "all") {
      filtered = filtered.filter(
        (athlete) => athlete.gender === selectedGender,
      );
    }

    // Sort alphabetically
    filtered.sort((a, b) => a.name.localeCompare(b.name, "pt-BR"));

    setFilteredAthletes(filtered);
  };

  const loadAthleteResults = async (athlete: Athlete) => {
    setIsLoading(true);
    setSelectedAthlete(athlete);
    setMessage("");

    try {
      // Load athlete score
      const score = await store.getAthleteScore(athlete.id);

      // Load all attempts
      const attempts = await store.getAthleteAttempts(athlete.id);

      // Calculate ranking
      const rankingData = await store.getRanking(athlete.gender);
      const athleteRanking = rankingData.findIndex(
        (entry) => entry.athlete.id === athlete.id,
      );

      const result: AthleteResult = {
        athlete,
        score,
        attempts,
        ranking: {
          position:
            athleteRanking >= 0 ? athleteRanking + 1 : rankingData.length + 1,
          total: rankingData.length,
          gender: athlete.gender,
        },
      };

      setAthleteResult(result);
    } catch (error) {
      console.error("Error loading athlete results:", error);
      setMessage("❌ Erro ao carregar resultados do atleta.");
      setAthleteResult(null);
    } finally {
      setIsLoading(false);
    }
  };

  const getRouteAttempts = (routeNumber: number): Attempt[] => {
    if (!athleteResult) return [];
    return athleteResult.attempts.filter(
      (attempt) => attempt.routeNumber === routeNumber,
    );
  };

  const getRouteStatus = (routeNumber: number) => {
    const attempts = getRouteAttempts(routeNumber);
    const completed = attempts.find((a) => a.result === "completed");

    if (completed) {
      return {
        status: completed.pointType as "flash" | "second+",
        points: completed.points,
        attempts: attempts.length,
        completed: true,
      };
    }

    return {
      status: attempts.length > 0 ? "failed" : "not-attempted",
      points: 0,
      attempts: attempts.length,
      completed: false,
    };
  };

  const getRankingColor = (position: number, total: number) => {
    const percentage = position / total;
    if (percentage <= 0.1) return "text-yellow-600"; // Top 10%
    if (percentage <= 0.25) return "text-green-600"; // Top 25%
    if (percentage <= 0.5) return "text-blue-600"; // Top 50%
    return "text-gray-600";
  };

  const getCompletedRoutes = () => {
    if (!athleteResult) return [];
    return athleteResult.attempts
      .filter((attempt) => attempt.result === "completed")
      .map((attempt) => attempt.routeNumber)
      .filter((route, index, self) => self.indexOf(route) === index)
      .sort((a, b) => a - b);
  };

  const getAttemptStats = () => {
    if (!athleteResult)
      return {
        total: 0,
        completed: 0,
        failed: 0,
        flashCount: 0,
        secondPlusCount: 0,
      };

    const completed = athleteResult.attempts.filter(
      (a) => a.result === "completed",
    );
    const failed = athleteResult.attempts.filter((a) => a.result === "failed");
    const flashCount = completed.filter((a) => a.pointType === "flash").length;
    const secondPlusCount = completed.filter(
      (a) => a.pointType === "second+",
    ).length;

    return {
      total: athleteResult.attempts.length,
      completed: completed.length,
      failed: failed.length,
      flashCount,
      secondPlusCount,
    };
  };

  const stats = getAttemptStats();
  const completedRoutes = getCompletedRoutes();

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-primary text-white py-4">
        <div className="container mx-auto px-4">
          <div className="flex items-center space-x-4">
            <Link to="/">
              <Button
                variant="ghost"
                size="sm"
                className="text-white hover:bg-white/20"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Voltar
              </Button>
            </Link>
            <h1 className="text-2xl font-bold">Consulta do Atleta</h1>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Search Panel */}
          <div className="lg:col-span-1">
            <Card>
              <CardHeader>
                <CardTitle>Buscar Atleta</CardTitle>
                <CardDescription>
                  Pesquise por nome ou selecione da lista
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="search">Nome do atleta</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                    <Input
                      id="search"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Digite o nome do atleta..."
                      className="pl-10"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="gender">Filtrar por gênero</Label>
                  <Select
                    value={selectedGender}
                    onValueChange={setSelectedGender}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      <SelectItem value="Masculino">Masculino</SelectItem>
                      <SelectItem value="Feminino">Feminino</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Athletes List */}
                <div>
                  <Label>Atletas ({filteredAthletes.length})</Label>
                  {isLoadingAthletes ? (
                    <div className="text-center py-4">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                      <p className="mt-2 text-sm text-gray-600">
                        Carregando...
                      </p>
                    </div>
                  ) : (
                    <div className="max-h-96 overflow-y-auto border rounded-md">
                      {filteredAthletes.length > 0 ? (
                        <div className="space-y-1 p-1">
                          {filteredAthletes.map((athlete) => (
                            <button
                              key={athlete.id}
                              type="button"
                              className={`w-full text-left p-3 rounded text-sm hover:bg-gray-100 transition-colors ${
                                selectedAthlete?.id === athlete.id
                                  ? "bg-primary text-white hover:bg-primary/90"
                                  : "bg-white"
                              }`}
                              onClick={() => loadAthleteResults(athlete)}
                              disabled={isLoading}
                            >
                              <div className="font-medium">{athlete.name}</div>
                              <div className="text-xs opacity-70">
                                {athlete.gender} | {athlete.timeSlot}
                              </div>
                            </button>
                          ))}
                        </div>
                      ) : (
                        <div className="p-4 text-center text-gray-500 text-sm">
                          {searchTerm
                            ? "Nenhum atleta encontrado"
                            : "Digite para buscar atletas"}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Results Panel */}
          <div className="lg:col-span-2">
            {message && (
              <Alert className="mb-6">
                <AlertDescription>{message}</AlertDescription>
              </Alert>
            )}

            {isLoading ? (
              <Card>
                <CardContent className="py-12">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
                    <p className="mt-4 text-gray-600">
                      Carregando resultados...
                    </p>
                  </div>
                </CardContent>
              </Card>
            ) : selectedAthlete && athleteResult ? (
              <div className="space-y-6">
                {/* Athlete Info */}
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-2xl">
                          {athleteResult.athlete.name}
                        </CardTitle>
                        <CardDescription>
                          {athleteResult.athlete.gender} |{" "}
                          {athleteResult.athlete.timeSlot}
                        </CardDescription>
                      </div>
                      <div className="text-right">
                        <div
                          className={`text-3xl font-bold ${getRankingColor(athleteResult.ranking.position, athleteResult.ranking.total)}`}
                        >
                          #{athleteResult.ranking.position}
                        </div>
                        <div className="text-sm text-gray-600">
                          de {athleteResult.ranking.total} atletas (
                          {athleteResult.ranking.gender})
                        </div>
                      </div>
                    </div>
                  </CardHeader>
                </Card>

                {/* Score Summary */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <Trophy className="w-5 h-5 mr-2" />
                      Pontuação Total
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="text-center">
                        <div className="text-3xl font-bold text-primary">
                          {athleteResult.score.totalScore}
                        </div>
                        <div className="text-sm text-gray-600">
                          Pontos Totais
                        </div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-green-600">
                          {stats.flashCount}
                        </div>
                        <div className="text-sm text-gray-600">Flash</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-blue-600">
                          {stats.secondPlusCount}
                        </div>
                        <div className="text-sm text-gray-600">2ª+</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-orange-600">
                          {completedRoutes.length}
                        </div>
                        <div className="text-sm text-gray-600">
                          Vias Completas
                        </div>
                      </div>
                    </div>

                    {athleteResult.score.topSixRoutes.length > 0 && (
                      <div className="mt-4 p-3 bg-yellow-50 rounded-lg">
                        <div className="text-sm font-medium text-yellow-800 mb-2">
                          Melhores 6 vias (pontuação final):
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {athleteResult.score.topSixRoutes.map(
                            (route, index) => (
                              <Badge
                                key={index}
                                variant="outline"
                                className="text-yellow-700 border-yellow-300"
                              >
                                Via {route.routeNumber}: {route.points}pts
                              </Badge>
                            ),
                          )}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Statistics */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <TrendingUp className="w-5 h-5 mr-2" />
                      Estatísticas
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="text-center">
                        <div className="text-xl font-bold">{stats.total}</div>
                        <div className="text-sm text-gray-600">
                          Total de Tentativas
                        </div>
                      </div>
                      <div className="text-center">
                        <div className="text-xl font-bold text-green-600">
                          {stats.completed}
                        </div>
                        <div className="text-sm text-gray-600">Sucessos</div>
                      </div>
                      <div className="text-center">
                        <div className="text-xl font-bold text-red-600">
                          {stats.failed}
                        </div>
                        <div className="text-sm text-gray-600">Falhas</div>
                      </div>
                      <div className="text-center">
                        <div className="text-xl font-bold text-blue-600">
                          {stats.total > 0
                            ? Math.round((stats.completed / stats.total) * 100)
                            : 0}
                          %
                        </div>
                        <div className="text-sm text-gray-600">
                          Taxa de Sucesso
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Routes Grid */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <Target className="w-5 h-5 mr-2" />
                      Mapa de Vias (1-40)
                    </CardTitle>
                    <CardDescription>
                      Status de cada via tentada pelo atleta
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-5 sm:grid-cols-8 lg:grid-cols-10 gap-2">
                      {Array.from({ length: 40 }, (_, i) => {
                        const routeNumber = i + 1;
                        const status = getRouteStatus(routeNumber);
                        const isTopSix = athleteResult.score.topSixRoutes.some(
                          (r) => r.routeNumber === routeNumber,
                        );

                        return (
                          <div
                            key={routeNumber}
                            className={`h-16 flex flex-col items-center justify-center text-xs font-medium rounded border-2 ${
                              status.completed
                                ? status.status === "flash"
                                  ? "bg-green-100 border-green-300 text-green-800"
                                  : "bg-blue-100 border-blue-300 text-blue-800"
                                : status.attempts > 0
                                  ? "bg-red-100 border-red-300 text-red-800"
                                  : "bg-gray-50 border-gray-200 text-gray-500"
                            } ${isTopSix ? "ring-2 ring-yellow-400" : ""}`}
                          >
                            <div className="font-bold">{routeNumber}</div>
                            {status.attempts > 0 && (
                              <div className="text-xs">
                                {status.completed
                                  ? status.status === "flash"
                                    ? "F"
                                    : "2+"
                                  : status.attempts}
                              </div>
                            )}
                            {isTopSix && (
                              <Star className="w-3 h-3 text-yellow-500" />
                            )}
                          </div>
                        );
                      })}
                    </div>

                    <div className="mt-4 flex flex-wrap justify-center gap-4 text-xs">
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 bg-green-100 border border-green-300 rounded"></div>
                        <span>Flash</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 bg-blue-100 border border-blue-300 rounded"></div>
                        <span>2ª+</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 bg-red-100 border border-red-300 rounded"></div>
                        <span>Falhou</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 bg-gray-50 border border-gray-200 rounded"></div>
                        <span>Não tentou</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Star className="w-4 h-4 text-yellow-500" />
                        <span>Top 6</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Recent Activity */}
                {athleteResult.attempts.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center">
                        <Clock className="w-5 h-5 mr-2" />
                        Atividade Recente
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {athleteResult.attempts
                          .sort(
                            (a, b) =>
                              new Date(b.timestamp).getTime() -
                              new Date(a.timestamp).getTime(),
                          )
                          .slice(0, 10)
                          .map((attempt, index) => (
                            <div
                              key={index}
                              className="flex items-center justify-between p-2 bg-gray-50 rounded"
                            >
                              <div className="flex items-center space-x-3">
                                <Badge
                                  variant={
                                    attempt.result === "completed"
                                      ? "default"
                                      : "destructive"
                                  }
                                >
                                  Via {attempt.routeNumber}
                                </Badge>
                                <span className="text-sm">
                                  {attempt.result === "completed"
                                    ? `${attempt.pointType === "flash" ? "Flash" : "2ª+"} (+${attempt.points}pts)`
                                    : "Falha"}
                                </span>
                              </div>
                              <span className="text-xs text-gray-500">
                                {new Date(attempt.timestamp).toLocaleString(
                                  "pt-BR",
                                )}
                              </span>
                            </div>
                          ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            ) : (
              <Card>
                <CardContent className="py-12">
                  <div className="text-center">
                    <Search className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      Selecione um atleta
                    </h3>
                    <p className="text-gray-600">
                      Use a busca ao lado para encontrar e consultar os
                      resultados de um atleta.
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
